// M140 — /merkstem — Merkstem-profiel (LLM-heuristiek + user-edits).
// M143: bevestigingsmodal voor Genereer opnieuw + toast-melding bij opslaan.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, Palette } from "lucide-react";

function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <div data-testid="merkstem-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="max-w-md w-full rounded-card border border-border-hairline bg-bg-panel p-5">
        <div className="text-sm font-display text-text-primary mb-2">{title}</div>
        <p className="text-xs text-text-primary leading-relaxed mb-4">{body}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="merkstem-confirm-cancel"
                   className="text-[11px] px-3 py-1.5 rounded border border-border-hairline text-text-muted">
            Annuleren
          </button>
          <button onClick={onConfirm} data-testid="merkstem-confirm-ok"
                   className="text-[11px] px-3 py-1.5 rounded border border-warn-amber/40 text-warn-amber hover:bg-warn-amber/10">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MerkstemPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ summary: "", tone: "", keywords: "", avoid: "" });
  const [err, setErr] = useState(null);
  const [askRegen, setAskRegen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/brand-voice");
      setData(data);
      if (data.exists) setForm({
        summary: data.summary || "",
        tone: data.tone || "",
        keywords: (data.keywords || []).join(", "),
        avoid: (data.avoid || []).join(", "),
      });
    } catch (e) { setErr("Kon merkstem niet laden."); }
  };
  useEffect(() => { load(); }, []);

  const regen = async () => {
    setBusy(true); setErr(null); setAskRegen(false);
    try {
      const { data } = await api.post("/brand-voice/regenerate");
      setData(data);
      setForm({ summary: data.summary || "", tone: data.tone || "",
                  keywords: (data.keywords || []).join(", "),
                  avoid: (data.avoid || []).join(", ") });
      toast.success("Merkstem opnieuw gemaakt.");
    } catch (e) {
      const msg = e?.response?.data?.detail?.reason_nl || "Genereren mislukt.";
      setErr(msg); toast.error(msg);
    }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.patch("/brand-voice", {
        summary: form.summary, tone: form.tone,
        keywords: form.keywords.split(",").map(s => s.trim()).filter(Boolean),
        avoid: form.avoid.split(",").map(s => s.trim()).filter(Boolean),
      });
      setData(data); setEditing(false);
      toast.success("Merkstem opgeslagen.");
    } catch (e) {
      setErr("Opslaan mislukt.");
      toast.error("Opslaan mislukt.");
    }
    finally { setBusy(false); }
  };

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
                 data-testid="merkstem-title">
              <Palette className="w-5 h-5 text-accent-teal" /> Merkstem
            </h1>
            <p className="text-sm text-text-muted mt-1">
              Hoe jouw bedrijf klinkt. JustEasySEO gebruikt dit voor elk advies en elke post.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAskRegen(true)} disabled={busy} data-testid="merkstem-regenerate"
                     className="text-sm px-3 py-2 rounded-[10px] border border-accent-teal/40 text-accent-teal-dim hover:bg-accent-mint flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Genereer opnieuw
            </button>
          </div>
        </div>
      )}
      {hideInnerHeader && (
        <div className="mb-4 flex justify-end">
          <button onClick={() => setAskRegen(true)} disabled={busy} data-testid="merkstem-regenerate"
                   className="text-sm px-3 py-2 rounded-[10px] border border-accent-teal/40 text-accent-teal-dim hover:bg-accent-mint flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Genereer opnieuw
          </button>
        </div>
      )}
      {err && <div className="text-xs text-warn-amber italic mb-4">{err}</div>}
      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Merkstem laden…
        </div>
      ) : !data.exists ? (
        <div className="rounded-card border border-border-hairline bg-bg-panel p-6 text-sm text-text-muted">
          {data.note_nl}
        </div>
      ) : (
        <div className="rounded-card border border-border-hairline bg-bg-panel p-5 space-y-3">
          {editing ? (
            <>
              <Field label="Kernboodschap (1 zin)" value={form.summary}
                      onChange={v => setForm({ ...form, summary: v })} data-testid="merkstem-summary" />
              <Field label="Toon" value={form.tone}
                      onChange={v => setForm({ ...form, tone: v })} data-testid="merkstem-tone" />
              <Field label="Kernwoorden (komma-gescheiden)" value={form.keywords}
                      onChange={v => setForm({ ...form, keywords: v })} data-testid="merkstem-keywords" />
              <Field label="Wat NIET past (komma-gescheiden)" value={form.avoid}
                      onChange={v => setForm({ ...form, avoid: v })} data-testid="merkstem-avoid" />
              <div className="flex gap-2">
                <button onClick={save} disabled={busy} data-testid="merkstem-save"
                         className="text-[11px] px-3 py-1.5 rounded border border-pos-green/40 hover:bg-pos-green/10 text-pos-green flex items-center gap-1 disabled:opacity-50">
                  <Save className="w-3 h-3" /> Opslaan
                </button>
                <button onClick={() => setEditing(false)}
                         className="text-[11px] px-3 py-1.5 rounded border border-border-hairline text-text-muted">Annuleren</button>
              </div>
            </>
          ) : (
            <>
              <Row k="Kernboodschap" v={data.summary} testid="merkstem-view-summary" />
              <Row k="Toon" v={data.tone} testid="merkstem-view-tone" />
              <Row k="Kernwoorden" v={(data.keywords || []).join(", ")} testid="merkstem-view-keywords" />
              <Row k="Wat NIET past" v={(data.avoid || []).join(", ")} testid="merkstem-view-avoid" />
              {(data.source_urls || []).length > 0 && (
                <div className="pt-2 border-t border-border-hairline">
                  <div className="text-[10px] text-text-muted mb-1">
                    Gebaseerd op
                  </div>
                  <div className="text-[11px] text-text-muted flex flex-wrap gap-2"
                        data-testid="merkstem-view-sources">
                    {(data.source_urls || []).map((u) => (
                      <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                          className="text-accent-teal hover:underline break-all">{u}</a>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setEditing(true)} data-testid="merkstem-edit"
                       className="mt-3 text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10">
                Bewerken
              </button>
            </>
          )}
        </div>
      )}
      {askRegen && (
        <ConfirmDialog
          title="Merkstem opnieuw genereren?"
          body={"Weet je het zeker? Jouw eigen aanpassingen in de merkstem worden overschreven "
                + "door de nieuw geanalyseerde versie."}
          confirmLabel="Ja, opnieuw genereren"
          onConfirm={regen} onCancel={() => setAskRegen(false)}
        />
      )}
    </AppLayout>
  );
}

function Row({ k, v, testid }) {
  return (
    <div>
      <div className="text-[10px] text-text-muted mb-1">{k}</div>
      <div className="text-sm text-text-primary" data-testid={testid}>
        {v || <span className="italic text-text-subtle">—</span>}
      </div>
    </div>
  );
}
function Field({ label, value, onChange, ...rest }) {
  return (
    <div>
      <div className="text-[10px] text-text-muted mb-1">{label}</div>
      <textarea value={value} onChange={e => onChange(e.target.value)}
                 rows={2} {...rest}
                 className="w-full text-sm bg-bg-panel-raised border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
    </div>
  );
}
